/**
 * Normaliza markdown gerado pelo LLM antes do render pra evitar 2 problemas:
 *
 *   (A) "Bullet órfão": linha solta com `- texto`, `* texto` ou `+ texto` é
 *       travessão narrativo, não item de lista. react-markdown vira bullet.
 *       Fix: escapa o marker com `\`.
 *
 *   (B) "Bullet-cabeçalho": item de lista que termina com `:` introduz uma
 *       sub-lista mas o LLM gera tudo flat no mesmo nível, então os items
 *       seguintes aparecem como irmãos do cabeçalho em vez de filhos. Fix:
 *       promove a linha pra parágrafo bold (sem marker) com linha em branco
 *       antes da próxima lista.
 *
 *   (C) "Setext acidental": em CommonMark, uma linha de texto seguida de
 *       `---` não é texto + régua — é um HEADING, com os traços fazendo de
 *       sublinhado. O LLM separa seções com `---` colado na última linha, e
 *       o resultado é um parágrafo inteiro renderizado em corpo de <h2>.
 *       Pior: o fix (A) acima ALIMENTA esse bug, porque transforma o bullet
 *       órfão em texto comum bem antes de um `---`. Fix: garante linha em
 *       branco entre o texto e a régua, o que faz o `---` voltar a ser
 *       régua e o texto voltar a ser texto.
 *
 * Heurística é conservadora pra não quebrar listas reais (2+ items
 * consecutivos sem `:` final). Idempotente.
 */
export function normalizeMarkdown(input: string): string {
  if (!input) return input;

  const lines = input.split("\n");
  const out: string[] = [];
  const bulletRe = /^[-*+]\s/;
  // Detecta sufixo `:` (com ou sem espaços/markdown bold no fim)
  const endsWithColonRe = /:\s*\**\s*$/;

  // (C) Régua (`---`, `===`, `***`) que vira sublinhado de setext quando
  // encostada no texto de cima. `***` não forma setext, mas entra na mesma
  // regra porque colado no parágrafo o remark também o engole.
  const ruleRe = /^\s*(?:-{3,}|={3,}|\*{3,})\s*$/;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    // `**bold**` no início é negrito, não bullet
    const looksLikeBullet = bulletRe.test(trimmed) && !/^\*\*/.test(trimmed);

    if (looksLikeBullet) {
      const prev = lines[i - 1]?.trim() ?? "";
      const next = lines[i + 1]?.trim() ?? "";
      const prevIsBullet = bulletRe.test(prev);
      const nextIsBullet = bulletRe.test(next);

      // (B) Cabeçalho introdutório: termina com `:` e é seguido por bullets.
      // Promove a parágrafo (sem marker), envolve em **bold** se ainda não
      // tem, e garante linha em branco antes da próxima lista pra remark
      // não engolir como continuação do anterior.
      if (endsWithColonRe.test(trimmed) && nextIsBullet) {
        const indent = line.slice(0, line.length - line.trimStart().length);
        const rest = trimmed.slice(1).trimStart(); // remove marker
        const hasBold = /^\*\*.*\*\*\s*[^*]*$|^\*\*.*\*\*\s*$/.test(rest);
        const promoted = hasBold ? rest : `**${rest}**`;
        out.push(`${indent}${promoted}`);
        out.push(""); // linha em branco pra separar lista
        continue;
      }

      // (A) Bullet órfão: nem o anterior nem o próximo são bullet → escapa
      if (!prevIsBullet && !nextIsBullet) {
        const marker = trimmed[0];
        const rest = trimmed.slice(1).trimStart();
        const indent = line.slice(0, line.length - line.trimStart().length);
        out.push(`${indent}\\${marker} ${rest}`);
        continue;
      }
    }

    if (ruleRe.test(line)) {
      const lastOut = out[out.length - 1];
      if (lastOut !== undefined && lastOut.trim() !== "") out.push("");
    }

    out.push(line);
  }

  return out.join("\n");
}
